/**
 * useGuideApprovals Hook
 *
 * React hook for fetching and managing guide approval workflow.
 * Handles approval queue, status updates, requirements, and history.
 *
 * Epic: MVP 1.21 - Comprehensive Dashboard & Account Management
 * Story: SCRUM-146 - Guide Approval Workflow Enhancement (34 SP)
 *
 * Usage:
 * const { queue, loading, error, refresh, approveGuide, rejectGuide } = useGuideApprovals(filters);
 */

import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import apiRequest from '../../services/api';

const API_BASE = '/api/v1/admin/guide-approvals';

/**
 * Hook for managing approval queue
 * @param {Object} initialFilters - Initial filters for the queue
 * @returns {Object} Queue data, loading states, and actions
 */
export function useGuideApprovals(initialFilters = {}) {
  const [queue, setQueue] = useState({ items: [], pagination: { total: 0, limit: 50, offset: 0, has_more: false } });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filters, setFilters] = useState(initialFilters);

  const fetchQueue = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams();
      if (filters.status) {params.append('status', filters.status);}
      if (filters.search) {params.append('search', filters.search);}
      if (filters.has_urgent) {params.append('has_urgent', 'true');}
      if (filters.sort_by) {params.append('sort_by', filters.sort_by);}
      if (filters.sort_order) {params.append('sort_order', filters.sort_order);}
      if (filters.limit) {params.append('limit', filters.limit);}
      if (filters.offset) {params.append('offset', filters.offset);}

      const response = await apiRequest(`${API_BASE}?${params.toString()}`, {
        method: 'GET'
      });

      if (response.success) {
        setQueue(response.data);
      } else {
        throw new Error('Failed to fetch approval queue');
      }
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchQueue();
  }, [fetchQueue]);

  const updateFilters = (newFilters) => {
    setFilters(prev => ({ ...prev, ...newFilters }));
  };

  const refresh = () => {
    fetchQueue();
  };

  return {
    queue,
    loading,
    error,
    filters,
    updateFilters,
    refresh,
  };
}

/**
 * Hook for managing individual guide approval
 * @param {string} guideId - Guide user ID
 * @returns {Object} Guide data, loading states, and actions
 */
export function useGuideApproval(guideId) {
  const [guide, setGuide] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  const fetchGuide = useCallback(async () => {
    if (!guideId) {return;}

    try {
      setLoading(true);
      setError(null);

      const response = await apiRequest(`${API_BASE}/${guideId}`, {
        method: 'GET'
      });

      if (response.success) {
        setGuide(response.data);
      } else {
        throw new Error('Failed to fetch guide details');
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  }, [guideId]);

  useEffect(() => {
    fetchGuide();
  }, [fetchGuide]);

  const approveGuide = async () => {
    try {
      setActionLoading(true);
      setError(null);

      const response = await axios.put(`${API_BASE}/${guideId}/approve`);

      if (response.data.success) {
        setGuide(response.data.data);
        return { success: true, data: response.data.data };
      } else {
        throw new Error('Failed to approve guide');
      }
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.message;
      setError(errorMsg);
      return { success: false, error: errorMsg };
    } finally {
      setActionLoading(false);
    }
  };

  const rejectGuide = async (reason, notes) => {
    try {
      setActionLoading(true);
      setError(null);

      const response = await axios.put(`${API_BASE}/${guideId}/reject`, {
        reason,
        notes,
      });

      if (response.data.success) {
        setGuide(response.data.data);
        return { success: true, data: response.data.data };
      } else {
        throw new Error('Failed to reject guide');
      }
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.message;
      setError(errorMsg);
      return { success: false, error: errorMsg };
    } finally {
      setActionLoading(false);
    }
  };

  const updateStatus = async (status, options = {}) => {
    try {
      setActionLoading(true);
      setError(null);

      const response = await axios.put(`${API_BASE}/${guideId}/status`, {
        status,
        notes: options.notes,
        expires_at: options.expiresAt,
        change_reason: options.changeReason,
      });

      if (response.data.success) {
        setGuide(response.data.data);
        return { success: true, data: response.data.data };
      } else {
        throw new Error('Failed to update status');
      }
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.message;
      setError(errorMsg);
      return { success: false, error: errorMsg };
    } finally {
      setActionLoading(false);
    }
  };

  const refresh = () => {
    fetchGuide();
  };

  return {
    guide,
    loading,
    error,
    actionLoading,
    approveGuide,
    rejectGuide,
    updateStatus,
    refresh,
  };
}

/**
 * Hook for managing guide requirements
 * @param {string} guideId - Guide user ID
 * @returns {Object} Requirements data, loading states, and actions
 */
export function useGuideRequirements(guideId) {
  const [requirements, setRequirements] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  const fetchRequirements = useCallback(async () => {
    if (!guideId) {return;}

    try {
      setLoading(true);
      setError(null);

      const response = await axios.get(`${API_BASE}/${guideId}/requirements`);

      if (response.data.success) {
        setRequirements(response.data.data);
      } else {
        throw new Error('Failed to fetch requirements');
      }
    } catch (err) {
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  }, [guideId]);

  useEffect(() => {
    fetchRequirements();
  }, [fetchRequirements]);

  const createRequirement = async (requirementData) => {
    try {
      setActionLoading(true);
      setError(null);

      const response = await axios.post(`${API_BASE}/${guideId}/requirements`, requirementData);

      if (response.data.success) {
        setRequirements(prev => [...prev, response.data.data]);
        return { success: true, data: response.data.data };
      } else {
        throw new Error('Failed to create requirement');
      }
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.message;
      setError(errorMsg);
      return { success: false, error: errorMsg };
    } finally {
      setActionLoading(false);
    }
  };

  const updateRequirementStatus = async (requirementId, status, reviewNotes) => {
    try {
      setActionLoading(true);
      setError(null);

      const response = await axios.put(`${API_BASE}/requirements/${requirementId}/status`, {
        status,
        review_notes: reviewNotes,
      });

      if (response.data.success) {
        setRequirements(prev =>
          prev.map(req => (req.id === requirementId ? response.data.data : req))
        );
        return { success: true, data: response.data.data };
      } else {
        throw new Error('Failed to update requirement');
      }
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.message;
      setError(errorMsg);
      return { success: false, error: errorMsg };
    } finally {
      setActionLoading(false);
    }
  };

  const deleteRequirement = async (requirementId) => {
    try {
      setActionLoading(true);
      setError(null);

      const response = await axios.delete(`${API_BASE}/requirements/${requirementId}`);

      if (response.data.success) {
        setRequirements(prev => prev.filter(req => req.id !== requirementId));
        return { success: true };
      } else {
        throw new Error('Failed to delete requirement');
      }
    } catch (err) {
      const errorMsg = err.response?.data?.error || err.message;
      setError(errorMsg);
      return { success: false, error: errorMsg };
    } finally {
      setActionLoading(false);
    }
  };

  const refresh = () => {
    fetchRequirements();
  };

  return {
    requirements,
    loading,
    error,
    actionLoading,
    createRequirement,
    updateRequirementStatus,
    deleteRequirement,
    refresh,
  };
}

export default useGuideApprovals;
